import { Pressable, StyleSheet, View } from "react-native";
import React, { useMemo } from "react";
import { Text } from "@ui-kitten/components";
import { useAppSelector } from "hooks/useAppSelector";
import { SignupChargeDetails } from "features/types";
import { useRoute } from "@react-navigation/native";
import { AntDesign } from "@expo/vector-icons";
import { useAppDispatch } from "hooks/useAppDispatch";
import {
  increaseAdultMemberQuantity,
  increaseChildMemberQuantity,
  reduceAdultMemberQuantity,
  reduceChildMemberQuantity,
} from "features/slices/addMembershipFlowSlice";
import { formatCurrency } from "utils/appUtils";
import { getPlanType } from "utils/planPackageUtils";

interface Props {
  type: "adult" | "child";
}
const QuantityModifier = (props: Props) => {
  const { type } = props;

  const dispatch = useAppDispatch();
  const { selectedBillingInterval, signupCharge, selectedPlanPackage } =
    useAppSelector((state) => state.buyFlow);
  const { adultMemberQuantity, childMemberQuantity } = useAppSelector(
    (state) => state.addMembershipFlow
  );
  const route = useRoute();

  const pricePerMembership = (signupCharge: SignupChargeDetails) => {
    if (!selectedPlanPackage) return "";
    if (selectedBillingInterval === "MONTHLY") {
      return formatCurrency(selectedPlanPackage.monthlyPrice);
    } else {
      // we want to show only the price for one member if billing interval is pay in full for
      // the current account
      return formatCurrency(
        (
          parseFloat(signupCharge.signupCharge) /
          signupCharge.planPackageIds.length
        ).toString()
      );
    }
  };
  const planType = selectedPlanPackage
    ? getPlanType(selectedPlanPackage)
    : undefined;

  const shouldShowQuantityModifier = useMemo(() => {
    if (!selectedPlanPackage) return false;
    switch (planType) {
      case "VOUCHER":
        return true;
      case "BANK":
        return false;
      case "RESERVATION":
        return route.name === "AddMembership";
      default:
        return true;
    }
  }, [selectedPlanPackage, planType]);

  const quantity = useMemo(() => {
    switch (route.name) {
      case "AddMembership": {
        return type === "adult" ? adultMemberQuantity : childMemberQuantity;
      }
      case "SubscriptionDetail": {
        return adultMemberQuantity;
      }
      default:
        return adultMemberQuantity;
    }
  }, [route.name, type, adultMemberQuantity, childMemberQuantity]);

  return (
    <View
      style={[
        styles.quantityModifier,
        type === "child" && styles.childQuantifyModifier,
      ]}
    >
      {/* For add subscription to account flow, don't show adult or child text */}
      <View>
        {route.name === "AddMembership" && (
          <Text>
            {type === "adult" ? "Adult" : "Child"}
            <Text category="c1">{type === "adult" ? "(AGES 13+)" : ""}</Text>
          </Text>
        )}

        {selectedBillingInterval === "MONTHLY" ? (
          <View>
            <View
              style={[
                { flexDirection: "row", alignItems: "flex-end" },
                styles.priceText,
              ]}
            >
              {signupCharge && (
                <Text category="h3">{pricePerMembership(signupCharge)}</Text>
              )}
              {/* https://github.com/FanRally/fanrally-core/issues/826 */}
              {selectedPlanPackage?.paymentModel === "INSTALLMENT" ? null : (
                <Text> / month</Text>
              )}
            </View>
          </View>
        ) : (
          <Text category="h3" style={styles.priceText}>
            {signupCharge ? pricePerMembership(signupCharge) : ""}
          </Text>
        )}
        <Text>
          <Text style={{ marginBottom: 10 }}>Per membership</Text>
        </Text>
      </View>
      <View>
        <Text style={{ marginBottom: 10 }}>
          <Text style={{ marginBottom: 10 }}>Quantity:</Text>
        </Text>
        <View style={{ flexDirection: "row", alignItems: "center" }}>
          {shouldShowQuantityModifier && (
            <Pressable
              onPress={() =>
                type === "adult"
                  ? dispatch(reduceAdultMemberQuantity())
                  : dispatch(reduceChildMemberQuantity())
              }
            >
              <AntDesign name="minuscircleo" size={20} color="black" />
            </Pressable>
          )}
          {/* if quantity is defined, show that, otherwise show the length of newMembers */}
          <Text style={{ marginHorizontal: 10 }} category="h5">
            {quantity}
          </Text>
          {shouldShowQuantityModifier && (
            <Pressable
              onPress={() =>
                type === "adult"
                  ? dispatch(increaseAdultMemberQuantity())
                  : dispatch(increaseChildMemberQuantity())
              }
            >
              <AntDesign name="pluscircleo" size={20} color="black" />
            </Pressable>
          )}
        </View>
      </View>
    </View>
  );
};
const styles = StyleSheet.create({
  priceText: {
    marginVertical: 5,
  },
  quantityModifier: {
    backgroundColor: "rgb(236, 236, 236)",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    padding: 15,
    borderRadius: 8,
  },
  childQuantifyModifier: {
    borderStyle: "dashed",
    borderWidth: 1,
  },
});

export default QuantityModifier;
